import { Popper, Stack, Typography } from '@mui/material';
import EntityMenu from 'components/entityMenu';
import { Droppable } from 'components/sidebar';
import { motion } from 'framer-motion';
import React, { useEffect } from 'react';

import { EntityCardView } from './components/EntityCardView';
import { EntityListView } from './components/EntityListView';
import { EntityTextView } from './components/EntityTextView';
import { useEntityViewController } from './index.controller';
import { EntityViewProps } from './index.types';

export type EntityViewPropsWithRef = EntityViewProps & {
  ref?: React.Ref<HTMLDivElement>;
};

export function EntityView(props: EntityViewPropsWithRef) {
  const {
    entity,
    viewType,
    hideType,
    hideParent,
    parentControlledCardSize,
    disableActions,
    searchParams,
    anchorEl,
    dragging,
    isSelected,
    workspaceId,
    copiedEntities,
    elemRef,
    popperRef,
    handleDrag,
    handleContextMenu,
    handleMoreButtonClick,
    handleListItemClick,
    handleFieldChange,
    setAnchorEl,
    isInClipboard,
  } = useEntityViewController(props);

  // Handle ref forwarding
  useEffect(() => {
    if (props.ref) {
      if (typeof props.ref === 'function') {
        props.ref(elemRef.current);
      } else if (props.ref) {
        (props.ref as React.MutableRefObject<HTMLDivElement | null>).current =
          elemRef.current;
      }
    }
  }, [props.ref, elemRef, props]);

  if (!entity) return null;

  let component: React.ReactNode;

  if (viewType === 'text') {
    component = (
      <EntityTextView entity={entity} searchParams={searchParams.toString()} />
    );
  } else if (viewType === 'card' && 'description' in entity) {
    component = (
      <EntityCardView
        entity={entity}
        searchParams={searchParams.toString()}
        hideParent={hideParent}
        parentControlledCardSize={parentControlledCardSize}
        disableActions={disableActions}
        elemRef={elemRef}
        onContextMenu={handleContextMenu}
        onDragStart={handleDrag}
        onMoreButtonClick={handleMoreButtonClick}
      />
    );
  } else if ('description' in entity) {
    component = (
      <EntityListView
        entity={entity}
        searchParams={searchParams.toString()}
        hideType={hideType}
        hideParent={hideParent}
        disableActions={disableActions}
        isSelected={isSelected}
        elemRef={elemRef}
        onContextMenu={handleContextMenu}
        onClick={handleListItemClick}
        onDragStart={handleDrag}
        onMoreButtonClick={handleMoreButtonClick}
        onFieldChange={handleFieldChange}
      />
    );
  } else {
    return null;
  }

  const elemSize = elemRef.current?.getBoundingClientRect();

  return (
    <>
      <Droppable
        alignTextLeft={viewType !== 'card'}
        boxProps={{
          sx: {
            pointerEvents: dragging ? 'none' : 'auto',
            opacity: dragging ? 0.4 : 1,
          },
        }}
        cachePurgeKeys={[
          ...('description' in entity
            ? [
                `/workspace/${workspaceId}/entities/${entity.types[0]}/root`,
                `/workspace/${workspaceId}/entities/${entity.types[0]}/all`,
              ]
            : []),
          `/workspace/${workspaceId}/entity/${entity.id}/children`,
        ]}
        parentId={entity.id}
      >
        {component}
      </Droppable>
      {'description' in entity && (
        <>
          <EntityMenu
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            entity={entity}
          />
          <Popper
            ref={popperRef}
            open={dragging}
            sx={{
              zIndex: 2000,
            }}
          >
            <motion.div
              initial={{
                width: elemSize?.width,
                height: elemSize?.height,
                opacity: 0.5,
              }}
              animate={{
                width: 0,
                height: 0,
                opacity: 1,
              }}
              transition={{
                // duration: 0.3,
                // ease: 'easeOut',
                type: 'spring',
                stiffness: 120,
                damping: 15,
                velocity: 0,
              }}
              style={{
                pointerEvents: 'none',
              }}
            >
              <Stack
                sx={{
                  width: '100%',
                  height: '100%',
                  minWidth: 'fit-content',
                  bgcolor: 'background.paper',
                  backgroundImage:
                    'linear-gradient(rgba(255, 255, 255, 0.09), rgba(255, 255, 255, 0.09))',
                  borderRadius: 1.5,
                  boxShadow: 15,
                  borderColor: 'divider',
                  borderWidth: 1,
                  borderStyle: 'solid',
                  direction: 'row',
                  justifyContent: 'center',
                  padding: 2.5,
                }}
              >
                <Typography whiteSpace="nowrap">
                  {copiedEntities.length > 0 && isInClipboard(entity.id)
                    ? `${entity.name.slice(0, 25)} + ${copiedEntities.length - 1} Others (NOT IMPLEMENTED)`
                    : entity.name}
                </Typography>
              </Stack>
            </motion.div>
          </Popper>
        </>
      )}
    </>
  );
}
